/**
 * Lightbox Gallery JavaScript
 * Handles product image lightbox functionality
 * Converted from TypeScript master to match exact functionality
 */

// Load GSAP helper (similar to side-cart.js)
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap) {
      resolve(window.gsap);
      return;
    }

    // Load GSAP from CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
    script.onload = () => resolve(window.gsap);
    script.onerror = () => {
      console.warn('GSAP failed to load, using fallback animations');
      resolve(null);
    };
    document.head.appendChild(script);
  });
}

class LightboxGallery {
  constructor(element) {
    this.host = element;
    this.closeBtn = this.host.querySelector('[lightbox-gallery-close]');
    this.carouselEl = this.host.querySelector('[carousel]');
    this.instance = null;
    this.gsap = null;

    // Touch screen detection
    this.isTouchScreen = window.matchMedia('(pointer: coarse)').matches;
    this.clickTreshold = this.isTouchScreen ? 50 : 200;
    this.clickTimer = null;
    this.lastMousedown = null;
    this.lastMousedownPos = null;

    this.init();
  }

  async init() {
    // Load GSAP
    this.gsap = await loadGSAP();

    // Initialize carousel if not already initialized
    if (this.carouselEl && !this.carouselEl.swiper) {
      if (typeof window.whenSwiperReady === 'function') {
        await window.whenSwiperReady(() => this.initCarousel());
      } else if (typeof Swiper !== 'undefined') {
        this.initCarousel();
      }
    }

    // Try to get instance from carousel element
    if (this.carouselEl) {
      // Check if Swiper is already initialized on this element
      if (this.carouselEl.swiper) {
        this.instance = this.carouselEl.swiper;
      } else {
        // Wait a bit for carousel to initialize, then try again
        // This handles cases where carousel is initialized by another script
        let retryCount = 0;
        const maxRetries = 20; // 2 seconds max wait
        const checkForSwiper = () => {
          if (this.carouselEl.swiper) {
            this.instance = this.carouselEl.swiper;
            this.bindEvents();
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkForSwiper, 100);
          } else {
            // If still not initialized after retries, try to initialize it ourselves
            if (typeof window.whenSwiperReady === 'function') {
              window.whenSwiperReady(() => {
                this.initCarousel();
                this.bindEvents();
              });
            } else if (typeof Swiper !== 'undefined') {
              this.initCarousel();
              this.bindEvents();
            }
          }
        };
        setTimeout(checkForSwiper, 100);
      }
    }

    // Bind events if instance is already available
    if (this.instance) {
      this.bindEvents();
    }
  }

  initCarousel() {
    if (!this.carouselEl) {
      return;
    }

    if (typeof Swiper === 'undefined') {
      return;
    }

    // Don't initialize if already initialized
    // Check both swiper property and swiper-initialized class
    if (this.carouselEl.swiper || this.carouselEl.classList.contains('swiper-initialized')) {
      if (this.carouselEl.swiper) {
        this.instance = this.carouselEl.swiper;
      }
      return;
    }

    const optionsAttr = this.carouselEl.getAttribute('carousel-options');
    let customOptions = {};
    
    if (optionsAttr) {
      try {
        // Try JSON.parse first (for valid JSON)
        customOptions = JSON.parse(optionsAttr);
      } catch (jsonError) {
        try {
          // If JSON.parse fails, try parsing as JavaScript object syntax
          // This handles Liquid-generated JavaScript object notation (e.g., { slidesPerView: 1 })
          customOptions = new Function('return ' + optionsAttr)();
        } catch (jsError) {
          console.warn('LightboxGallery: Invalid carousel-options format', jsError);
          // Use default options if parsing fails
          customOptions = {};
        }
      }
    }

    // Fix navigation selectors - they should be scoped to this.host (lightboxGallery element)
    // The carousel-options has selectors like '.lightboxGallery .swiper-nav--next'
    // We need to find the actual button elements within this.host
    if (customOptions.navigation) {
      // Try to find buttons using the selector from options
      let nextEl = null;
      let prevEl = null;
      
      // If selector is a string, try to find the element
      if (typeof customOptions.navigation.nextEl === 'string') {
        // Remove '.lightboxGallery' prefix if present and find within host
        const nextSelector = customOptions.navigation.nextEl.replace(/^\.lightboxGallery\s+/, '');
        nextEl = this.host.querySelector(nextSelector);
      }
      
      if (typeof customOptions.navigation.prevEl === 'string') {
        const prevSelector = customOptions.navigation.prevEl.replace(/^\.lightboxGallery\s+/, '');
        prevEl = this.host.querySelector(prevSelector);
      }
      
      // Fallback: try to find buttons directly by class
      if (!nextEl || !prevEl) {
        const nextBtn = this.host.querySelector('.swiper-nav--next');
        const prevBtn = this.host.querySelector('.swiper-nav--prev');
        if (nextBtn) nextEl = nextBtn;
        if (prevBtn) prevEl = prevBtn;
      }
      
      if (nextEl && prevEl) {
        customOptions.navigation = {
          nextEl: nextEl,
          prevEl: prevEl
        };
      } else {
        // If buttons not found, remove navigation to prevent errors
        delete customOptions.navigation;
      }
    }

    // Initialize Swiper with options from carousel-options attribute
    // Make sure the element has swiper-wrapper and is not already initialized
    if (this.carouselEl.querySelector('.swiper-wrapper') && !this.carouselEl.swiper) {
      try {
        this.instance = new Swiper(this.carouselEl, customOptions);
        this.carouselEl.swiper = this.instance;
        
        // Ensure navigation is properly initialized
        if (this.instance.navigation && this.instance.navigation.nextEl && this.instance.navigation.prevEl) {
          this.instance.navigation.init();
          this.instance.navigation.update();
        }
      } catch (error) {
        console.warn('LightboxGallery: Error initializing Swiper', error);
      }
    } else if (this.carouselEl.swiper) {
      // Already initialized, just get the instance
      this.instance = this.carouselEl.swiper;
    }
  }

  bindEvents() {
    if (!this.instance) {
      // Retry binding after a short delay
      setTimeout(() => {
        if (this.carouselEl && this.carouselEl.swiper) {
          this.instance = this.carouselEl.swiper;
          this.bindEvents();
        }
      }, 200);
      return;
    }

    // Bind Swiper events
    this.instance.on('touchStart', this.onMouseDown.bind(this));
    this.instance.on('touchEnd', this.onMouseUp.bind(this));
    this.instance.on('slideChange', this.onSlideChange.bind(this));
    this.instance.on('slideChangeTransitionEnd', this.removeZoomClass.bind(this));

    // Bind close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.onCloseBtnClick.bind(this));
    }

    // Bind keyboard events for accessibility
    this.host.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  onSlideChange(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) {
      return;
    }

    const video = activeSlide.querySelector('video');

    if (video) {
      video.play();
    } else {
      const galleryVideos = this.host.querySelectorAll('video');
      if (galleryVideos) {
        galleryVideos.forEach(v => v.pause());
      }
    }
  }

  onMouseDown(swiper, e) {
    const { clickTimer, clickTreshold } = this;

    if (this.getZoomedEl()) {
      this.updateSwipeEvent(false);
    }

    this.lastMousedown = e.timeStamp;
    this.lastMousedownPos = {
      x: e.clientX,
      y: e.clientY
    };

    clearTimeout(clickTimer);

    this.clickTimer = window.setTimeout(() => {
      this.lastMousedown = null;
      this.lastMousedownPos = null;
    }, clickTreshold);
  }

  updateSwipeEvent(allowSwipe) {
    if (this.instance) {
      this.instance.allowSlideNext = allowSwipe;
      this.instance.allowSlidePrev = allowSwipe;
    }
  }

  onMouseUp(swiper, e) {
    const { lastMousedown, lastMousedownPos, clickTreshold } = this;
    this.updateSwipeEvent(true);

    if (!lastMousedown) return;

    const pos = {
      x: e.clientX,
      y: e.clientY
    };

    const distance = Math.hypot(pos.x - lastMousedownPos.x, pos.y - lastMousedownPos.y);

    if (distance > 10) {
      return;
    }

    if (e.timeStamp - this.lastMousedown <= clickTreshold) {
      if (this.instance && this.instance.zoom) {
        this.instance.zoom.toggle();
      }
    }
  }

  onCloseBtnClick(e) {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.blur();
    }
    this.close();
  }

  onKeyDown(e) {
    // ESC key closes lightbox
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    // Arrow keys for navigation (only when lightbox is open/visible)
    if (this.host.style.opacity !== '0' && this.host.style.visibility !== 'hidden') {
      if (e.key === 'ArrowLeft' && this.instance) {
        e.preventDefault();
        this.instance.slidePrev();
      } else if (e.key === 'ArrowRight' && this.instance) {
        e.preventDefault();
        this.instance.slideNext();
      }
    }
  }

  removeZoomClass() {
    const zoomedEl = this.getZoomedEl();
    if (zoomedEl) {
      zoomedEl.classList.remove('swiper-slide-zoomed');
    }
  }

  getZoomedEl() {
    if (!this.instance) return null;
    return this.instance.el.querySelector('.swiper-slide-zoomed');
  }

  open() {
    if (!this.instance) {
      // Try to initialize if not already done
      if (this.carouselEl && !this.carouselEl.swiper) {
        this.initCarousel();
      } else if (this.carouselEl && this.carouselEl.swiper) {
        this.instance = this.carouselEl.swiper;
        this.bindEvents();
      }
    }

    if (!this.instance) {
      console.warn('LightboxGallery: Cannot open - Swiper instance not available');
      return;
    }

    this.instance.enable();
    this.instance.update();
    
    // Update navigation buttons state
    if (this.instance.navigation) {
      this.instance.navigation.update();
    }

    if (this.gsap) {
      // Use GSAP animations (matches TypeScript master)
      this.gsap.to(this.host, {
        autoAlpha: 1,
        duration: 0.25,
        onComplete: () => {
          this.host.setAttribute('tabindex', '0');
          this.host.focus();
          document.body.classList.add('has-dialog');
          this.updateLayout();
        }
      });
    } else {
      // Fallback to CSS transitions if GSAP not available
      this.host.style.opacity = '1';
      this.host.style.visibility = 'inherit';
      this.host.setAttribute('tabindex', '0');
      this.host.focus();
      document.body.classList.add('has-dialog');
      // Update layout after visibility change
      setTimeout(() => this.updateLayout(), 100);
    }
  }

  /**
   * Recalculate Swiper dimensions after lightbox becomes visible.
   * Needed on mobile where flex + 100dvh layout is applied after open.
   */
  updateLayout() {
    if (!this.instance) return;

    requestAnimationFrame(() => {
      this.instance.updateSize?.();
      this.instance.update?.();

      if (this.instance.navigation) {
        this.instance.navigation.update();
      }

      if (this.instance.thumbs?.swiper) {
        this.instance.thumbs.swiper.updateSize?.();
        this.instance.thumbs.swiper.update?.();
      }
    });
  }

  close() {
    if (!this.instance) return;

    this.instance.disable();

    if (this.gsap) {
      // Use GSAP animations (matches TypeScript master)
      this.gsap.to(this.host, {
        autoAlpha: 0,
        duration: 0.25,
        onComplete: () => {
          this.host.setAttribute('tabindex', '-1');
          document.body.classList.remove('has-dialog');
        }
      });
    } else {
      // Fallback to CSS transitions if GSAP not available
      this.host.style.opacity = '0';
      this.host.style.visibility = 'hidden';
      this.host.setAttribute('tabindex', '-1');
      document.body.classList.remove('has-dialog');
    }
  }
}

// Initialize lightbox galleries
function initializeLightboxGalleries() {
  const lightboxGalleries = document.querySelectorAll('[lightbox-gallery]');

  lightboxGalleries.forEach((gallery) => {
    if (!gallery.lightboxGalleryInstance) {
      gallery.lightboxGalleryInstance = new LightboxGallery(gallery);
    }
  });
}

function startLightboxInitialization() {
  const run = () => initializeLightboxGalleries();

  if (typeof window.whenSwiperReady === 'function') {
    window.whenSwiperReady(run);
    return;
  }

  if (typeof Swiper !== 'undefined') {
    run();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startLightboxInitialization);
} else {
  startLightboxInitialization();
}

// Re-initialize on section loads (Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  initializeLightboxGalleries();
});

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.LightboxGallery = LightboxGallery;
}

